import { ausRelay, JE_PRUEFUNG, leseEinstellungen, type Gemischt, type Schluessel, type Wert } from '../platform/einstellungen';
import type { ClientRow, ExamRow } from './rows';
import type { Store } from './port';
import { leseQrUeberschreibungen } from './qrUeberschreibungen';
import { loadSettings, saveSetting } from './settings';

/**
 * Die zwei Stufen der Rangfolge, die nur für die ausgewählte Prüfung gelten (Entscheidung des
 * Nutzers, 2026-10-10, mit Relay abgestimmt): `pruefung` — was Relay für diese Prüfung setzt
 * (`Exam.settings`) — und darüber `appPruefung` — was der Prüfer in der App geändert hat,
 * solange sie ausgewählt ist.
 *
 * ⚠️ **Die App-Änderungen fallen weg, sobald eine andere oder keine Prüfung ausgewählt ist.**
 * Sie gehen deshalb nicht ins iPad (sonst überlebten sie die Prüfung), sondern in die
 * App-Ablage unter eigenem Vorsatz — dieselbe Bauform wie `qrUeberschreibungen.ts`. Die Zeile
 * `MERKER` sagt, zu welcher Prüfung sie gehören; ein Neustart mit derselben Prüfung behält sie.
 */

const VORSATZ = 'pruefung.';
const MERKER = 'pruefung@';

/** Relays `settings` in die Ablageform: nur `JE_PRUEFUNG`, nur Text. Der Rest wird genannt. */
export function pruefungsEinstellungenAblegen(roh: unknown): { ablage: string | null; verworfen: string[] } {
  if (typeof roh !== 'object' || roh === null || Array.isArray(roh)) return { ablage: null, verworfen: [] };
  const behalten: Record<string, string> = {};
  const verworfen: string[] = [];
  for (const [name, wert] of Object.entries(roh)) {
    if (JE_PRUEFUNG.has(name as Schluessel) && typeof wert === 'string') behalten[name] = wert;
    else verworfen.push(name);
  }
  return { ablage: Object.keys(behalten).length > 0 ? JSON.stringify(behalten) : null, verworfen };
}

function nurJePruefung(roh: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(roh).filter(
      (e): e is [string, string] => JE_PRUEFUNG.has(e[0] as Schluessel) && typeof e[1] === 'string',
    ),
  );
}

/** Die abgelegten Werte einer Prüfung als Text, nur `JE_PRUEFUNG`. Kaputtes JSON: keine. */
export function pruefungsEinstellungenRoh(ablage: string | null | undefined): Record<string, string> {
  if (!ablage) return {};
  try {
    const roh: unknown = JSON.parse(ablage);
    return typeof roh === 'object' && roh !== null ? nurJePruefung(roh) : {};
  } catch {
    return {};
  }
}

/** Beide Stufen der ausgewählten Prüfung, typisiert wie jede andere Quelle. */
export async function lesePruefungsStufen(
  store: Store,
  clientId: string,
  examId: string,
): Promise<{ pruefung: Partial<Record<Schluessel, unknown>>; appPruefung: Partial<Record<Schluessel, unknown>> }> {
  if (!examId) return { pruefung: {}, appPruefung: {} };
  const [exam] = await store.all<ExamRow>('Exam', { id: examId });
  const alle = clientId ? await loadSettings(store, clientId) : {};
  const geaendert: Record<string, string> = {};
  if (alle[MERKER] === examId) {
    for (const [name, roh] of Object.entries(alle)) {
      if (name.startsWith(VORSATZ) && roh !== '') geaendert[name.slice(VORSATZ.length)] = roh;
    }
  }
  return {
    pruefung: ausRelay(pruefungsEinstellungenRoh(exam?.settings)),
    appPruefung: ausRelay(nurJePruefung(geaendert)),
  };
}

/** Eine App-Änderung für die ausgewählte Prüfung merken. Gehörte Früheres zu einer anderen: weg. */
export async function schreibeAppJePruefung(
  store: Store,
  clientId: string,
  examId: string,
  schluessel: Schluessel,
  wert: Wert,
): Promise<void> {
  await verwirfAppJePruefung(store, clientId, examId);
  await saveSetting(store, clientId, MERKER, examId);
  await saveSetting(store, clientId, VORSATZ + schluessel, String(wert));
}

/**
 * Die App-Änderungen verwerfen, wenn sie nicht zu `behalten` gehören. Leer heisst wie bei den
 * QR-Werten „zurückgenommen“ — die Ablage kennt kein Löschen über `saveSetting`.
 */
export async function verwirfAppJePruefung(store: Store, clientId: string, behalten = ''): Promise<void> {
  if (!clientId) return;
  const alle = await loadSettings(store, clientId);
  if (!alle[MERKER] || alle[MERKER] === behalten) return;
  for (const name of Object.keys(alle)) {
    if (name.startsWith(VORSATZ) && alle[name] !== '') await saveSetting(store, clientId, name, '');
  }
  await saveSetting(store, clientId, MERKER, '');
}

/**
 * Alle Quellen frisch lesen und mischen — **die** Mischung, für Start, Dialog, QR-Code und die
 * Takte von Relay und Classic. `examId` fehlt: die ausgewählte Prüfung aus der Gerätezeile.
 *
 * ⚠️ **Ohne die Prüfungsstufen hier** löschte jedes Fernsetzen von Relay (das danach neu
 * mischt und übernimmt) die Einstellungen der laufenden Prüfung bis zum nächsten Wechsel.
 */
export async function ladeGemischt(store: Store, clientId: string, examId?: string): Promise<Gemischt> {
  const pruefung =
    examId ?? (clientId ? (await store.all<ClientRow>('Client', { id: clientId }))[0]?.currentExamID ?? '' : '');
  const [app, qr, stufen] = await Promise.all([
    clientId ? loadSettings(store, clientId) : Promise.resolve({}),
    clientId ? leseQrUeberschreibungen(store, clientId).catch(() => ({})) : Promise.resolve({}),
    lesePruefungsStufen(store, clientId, pruefung),
  ]);
  return leseEinstellungen(ausRelay(app), qr, stufen);
}
